// app/javascript/landing/sections/ImmersiveBenefitsSection.tsx
import React, { useEffect, useState } from "react";
import { motion, Transition, Variants } from "framer-motion";

const transition: Transition = { duration: 1, ease: [0.25, 0.1, 0.25, 1] };
const variants: Variants = {
  hidden: { opacity: 0, y: 20, filter: "blur(6px)" },
  visible: { opacity: 1, y: 0, filter: "blur(0)" },
};

// Count-up hook
function useCountUp(target: number, start: boolean, duration = 1600) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!start) return;

    let frame: number;
    const startTime = performance.now();

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1);
      setValue(Math.round(target * progress));
      if (progress < 1) frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [start, target, duration]);

  return value;
}

export function ImmersiveBenefitsSection() {
  const [started, setStarted] = useState(false);

  const vRetention = useCountUp(75, started);
  const vEfficiency = useCountUp(50, started);
  const vReduction = useCountUp(40, started);

  return (
    <motion.section
      className="relative w-full bg-purple-3 text-white pt-32 pb-6 overflow-hidden"
      initial="hidden"
      whileInView="visible"
      transition={{ staggerChildren: 0.12 }}
    >
      {/* BACKGROUND BUILDINGS */}
      <div className="absolute inset-0 flex justify-center items-end pointer-events-none">
        <img
          src="/landing/immersive/buildings.svg"
          className="w-full max-w-7xl opacity-80 select-none"
          alt=""
        />
      </div>

      {/* CONTENT */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 text-center space-y-32">
        {/* PARTICLES (logo girando) */}
        <motion.img
          src="/landing/logos/logo-icon.png"
          alt=""
          className="absolute top-10 left-10 w-16 opacity-40 pointer-events-none"
          animate={{ rotate: 360 }}
          transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
        />

        <motion.img
          src="/landing/logos/logo-icon.png"
          alt=""
          className="absolute top-10 right-10 w-16 opacity-40 pointer-events-none"
          animate={{ rotate: -360 }}
          transition={{ duration: 22, repeat: Infinity, ease: "linear" }}
        />

        {/* TITLE */}
        <motion.h2
          variants={variants}
          transition={transition}
          className="text-3xl md:text-4xl font-bold"
        >
          ¿Por qué experiencias inmersivas?
        </motion.h2>

        {/* STATS */}
        <motion.div
          variants={variants}
          transition={transition}
          className="grid grid-cols-1 md:grid-cols-3 gap-12"
          onViewportEnter={() => setStarted(true)}
        >
          {/* ITEM 01 */}
          <div className="space-y-3">
            <div className="text-5xl font-black">{vRetention}%</div>
            <div className="text-lg leading-tight">
              <span className="highlight-marker">
                Retención de conocimiento
              </span>
              <br />
              en capacitaciones.
            </div>
          </div>

          {/* ITEM 02 */}
          <div className="space-y-3">
            <div className="text-5xl font-black">+{vEfficiency}%</div>
            <div className="text-lg leading-tight">
              <span className="highlight-marker">
                Eficiencia en tareas complejas
              </span>
              <br />
              con entrenamiento VR.
            </div>
          </div>

          {/* ITEM 03 */}
          <div className="space-y-3">
            <div className="text-5xl font-black">{vReduction}%</div>
            <div className="text-lg leading-tight">
              <span className="highlight-marker">Reducción en errores</span>
              <br />
              operacionales.
            </div>
          </div>
        </motion.div>

        {/* FOOTNOTE */}
        <motion.p
          variants={variants}
          transition={transition}
          className="text-xs text-white/70 mt-8 text-right"
        >
          Fuente: Asociación Chilena de Seguridad.
        </motion.p>
      </div>
    </motion.section>
  );
}
